import { useState, useEffect, useRef } from 'react';
import { Alert, Button, DateDisplay, Loading, Modal, Paragraph, useToast } from '@aidkitorg/component-library';
import { usePost } from '../API';


interface LastRun {
  taskId: string;
  createdAt: string;
  createdBy: string | null;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  erroredAt: string | null;
  result: { success: boolean; error?: string } | null;
}

interface DevPortalParam {
  name: string;
  label?: string;
  required: boolean;
  description?: string;
  options?: readonly string[];
}

interface DevTool {
  name: string;
  description: string;
  params: DevPortalParam[];
  lastRun: LastRun | null;
}

type JobStatus = 'QUEUED' | 'STARTED' | 'FINISHED' | 'FAILED' | 'NOT_FOUND';

interface JobState {
  taskId: string;
  triggeredAt: string;
  status: JobStatus;
  result: { success: boolean; error?: string } | null;
  startedAt: string | null;
  finishedAt: string | null;
  erroredAt: string | null;
}

const TERMINAL_STATUSES: JobStatus[] = ['FINISHED', 'FAILED', 'NOT_FOUND'];
const VALID_STATUSES: JobStatus[] = ['QUEUED', 'STARTED', 'FINISHED', 'FAILED', 'NOT_FOUND'];
const isJobStatus = (s: string): s is JobStatus => VALID_STATUSES.includes(s as JobStatus);

function formatDuration(startedAt: string | null, finishedAt: string | null, erroredAt: string | null): string | null {
  const end = finishedAt ?? erroredAt;
  if (!startedAt || !end) return null;
  const ms = new Date(end).getTime() - new Date(startedAt).getTime();
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

interface LogModal {
  toolName: string;
  taskId: string;
}

export const DevToolsPage = () => {
  const [tools, setTools] = useState<DevTool[] | null>(null);
  const [isProduction, setIsProduction] = useState(false);
  const [triggering, setTriggering] = useState<Set<string>>(new Set());
  const [jobStates, setJobStates] = useState<Map<string, JobState>>(new Map());
  const [completedRuns, setCompletedRuns] = useState<Map<string, LastRun>>(new Map());
  const [confirmationModal, setConfirmationModal] = useState<DevTool | null>(null);
  const [confirmParams, setConfirmParams] = useState<Record<string, string>>({});

  // Log viewer state
  const [logModal, setLogModal] = useState<LogModal | null>(null);
  const [logText, setLogText] = useState('');
  const logNextSeqRef = useRef(0);
  const logEndRef = useRef<HTMLDivElement>(null);

  const { toast } = useToast();

  const listTools = usePost('/dev/tools/list');
  const triggerTool = usePost('/dev/tools/trigger');
  const getJobResult = usePost('/dev/tools/job_result');
  const getJobLogs = usePost('/dev/tools/job_logs');

  // Stable refs so effect closures don't capture stale values
  const getJobResultRef = useRef(getJobResult);
  getJobResultRef.current = getJobResult;
  const getJobLogsRef = useRef(getJobLogs);
  getJobLogsRef.current = getJobLogs;

  useEffect(() => {
    listTools({} as never).then(response => {
      if (response && typeof response === 'object' && response !== null && 'redirectTo' in response) {
        const url = Reflect.get(response, 'redirectTo');
        if (typeof url === 'string' && url.length > 0) {
          window.location.replace(url);
          return;
        }
      }
      if (response && typeof response === 'object' && response !== null && 'tools' in response) {
        const toolsList = Reflect.get(response, 'tools');
        if (Array.isArray(toolsList)) {
          setTools(toolsList);
          setIsProduction(Reflect.get(response, 'isProduction') === true);
        }
      }
    });
  }, []);

  // Poll in-flight jobs every 2s
  useEffect(() => {
    const inFlight = Array.from(jobStates.entries()).filter(
      ([, j]) => !TERMINAL_STATUSES.includes(j.status),
    );
    if (inFlight.length === 0) return;

    const timer = setTimeout(async () => {
      for (const [toolName, job] of inFlight) {
        try {
          const response = await getJobResultRef.current({ taskId: job.taskId });
          const rawStatus: string = response.status ?? '';
          const nextStatus: JobStatus = isJobStatus(rawStatus) ? rawStatus : 'STARTED';
          const updatedJob: JobState = {
            ...job,
            status: nextStatus,
            result: response.result ?? null,
            startedAt: response.startedAt ?? null,
            finishedAt: response.finishedAt ?? null,
            erroredAt: response.erroredAt ?? null,
          };
          setJobStates(prev => { const next = new Map(prev); next.set(toolName, updatedJob); return next; });
          if (TERMINAL_STATUSES.includes(nextStatus)) {
            setCompletedRuns(prev => new Map(prev).set(toolName, {
              taskId: job.taskId,
              createdAt: updatedJob.triggeredAt,
              createdBy: response.createdBy ?? null,
              status: nextStatus,
              startedAt: updatedJob.startedAt,
              finishedAt: updatedJob.finishedAt,
              erroredAt: updatedJob.erroredAt,
              result: updatedJob.result,
            }));
          }
        } catch {
          // network error — keep polling
        }
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [jobStates]);

  // Poll log chunks while the log modal is open (immediately, then every 2 s)
  useEffect(() => {
    if (!logModal) return;

    let cancelled = false;

    const fetchLogs = async () => {
      if (cancelled) return;
      try {
        const response = await getJobLogsRef.current({
          taskId: logModal.taskId,
          afterSeq: logNextSeqRef.current,
        });
        if (cancelled) return;
        if (response.chunks.length > 0) {
          setLogText(prev => prev + response.chunks.map(c => c.text).join(''));
          logNextSeqRef.current = response.nextSeq;
        }
      } catch {
        // ignore network errors
      }
    };

    void fetchLogs();
    const interval = setInterval(() => { void fetchLogs(); }, 2000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [logModal]);

  // Auto-scroll to bottom of log output when new text arrives
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logText]);

  const openLogModal = (toolName: string, taskId: string) => {
    logNextSeqRef.current = 0;
    setLogText('');
    setLogModal({ toolName, taskId });
  };

  const confirmTrigger = async () => {
    if (!confirmationModal) return;
    const { name } = confirmationModal;
    const toolParams = { ...confirmParams };
    setConfirmationModal(null);
    setConfirmParams({});
    setTriggering(prev => new Set(prev).add(name));
    setJobStates(prev => { const next = new Map(prev); next.delete(name); return next; });

    try {
      const response = await triggerTool({ toolName: name, toolParams });
      if ('error' in response) {
        toast({ title: 'Failed to queue tool', description: response.error, variant: 'error' });
      } else {
        setJobStates(prev => new Map(prev).set(name, {
          taskId: response.taskId,
          triggeredAt: new Date().toISOString(),
          status: 'QUEUED',
          result: null,
          startedAt: null,
          finishedAt: null,
          erroredAt: null,
        }));
      }
    } catch (err) {
      toast({ title: 'Failed to queue tool', description: err instanceof Error ? err.message : String(err), variant: 'error' });
    } finally {
      setTriggering(prev => { const next = new Set(prev); next.delete(name); return next; });
    }
  };

  if (tools === null) return <Loading />;

  return (
    <>
      {confirmationModal && (() => {
        const missingRequired = confirmationModal.params.some(
          p => p.required && !confirmParams[p.name],
        );
        return (
          <Modal
            open
            onClose={() => setConfirmationModal(null)}
            title={`Run ${confirmationModal.name}`}
          >
            <Paragraph measure="wide">
              This will run <strong>{confirmationModal.name}</strong> across all programs.
              {confirmationModal.description && <><br /><br />{confirmationModal.description}</>}
            </Paragraph>

            {confirmationModal.params.length > 0 && (
              <div className="mt-4 space-y-3">
                {confirmationModal.params.map(param => (
                  <div key={param.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {param.label ?? param.name}
                      {param.required && <span className="ml-1 text-red-500">*</span>}
                    </label>
                    {param.description && (
                      <p className="mb-1 text-xs text-gray-500">{param.description}</p>
                    )}
                    {param.options ? (
                      <select
                        className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        value={confirmParams[param.name] ?? ''}
                        onChange={e => setConfirmParams(prev => ({ ...prev, [param.name]: e.target.value }))}
                      >
                        <option value="">Select…</option>
                        {param.options.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        value={confirmParams[param.name] ?? ''}
                        onChange={e => setConfirmParams(prev => ({ ...prev, [param.name]: e.target.value }))}
                        placeholder={param.label ?? param.name}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            <Paragraph measure="wide" className="mt-4">Are you sure?</Paragraph>
            <div className="flex flex-row justify-end gap-x-3">
              <Button variant="secondary" onClick={() => setConfirmationModal(null)}>Cancel</Button>
              <Button variant="danger" onClick={confirmTrigger} disabled={missingRequired}>Run now</Button>
            </div>
          </Modal>
        );
      })()}

      {logModal && (
        <Modal
          open
          onClose={() => setLogModal(null)}
          title={`Logs — ${logModal.toolName}`}
          size="fullscreen"
        >
          <div className="overflow-auto rounded bg-gray-900" style={{ maxHeight: '60vh' }}>
            <pre className="p-3 text-xs font-mono text-gray-100 whitespace-pre-wrap break-all">
              {logText || '(no output captured yet)'}
            </pre>
            <div ref={logEndRef} />
          </div>
          <div className="flex justify-end mt-3">
            <Button variant="secondary" onClick={() => setLogModal(null)}>Close</Button>
          </div>
        </Modal>
      )}

      {isProduction && (
        <Alert variant="warning" mode="block" className="mx-4 sm:mx-6 lg:mx-8 mt-6" title="Production environment">
          You are running tools against production data. Proceed with caution.
        </Alert>
      )}

      <div className="px-4 sm:px-6 lg:px-8 mt-8">
        <ul className="divide-y divide-gray-200">
          {tools.map(tool => {
            const isTriggering = triggering.has(tool.name);
            const job = jobStates.get(tool.name);
            const isInFlight = job ? !TERMINAL_STATUSES.includes(job.status) : false;
            const isBusy = isTriggering || isInFlight;

            const effectiveLastRun: LastRun | null =
              completedRuns.get(tool.name) ?? tool.lastRun;
            const showingCurrentRun = isTriggering || isInFlight;

            const displayTimestamp = showingCurrentRun
              ? (job?.triggeredAt ?? null)
              : (effectiveLastRun?.finishedAt ?? effectiveLastRun?.erroredAt ?? effectiveLastRun?.createdAt ?? null);
            const duration = showingCurrentRun
              ? null
              : (effectiveLastRun
                ? formatDuration(effectiveLastRun.startedAt, effectiveLastRun.finishedAt, effectiveLastRun.erroredAt)
                : null);

            const logsTaskId = job?.taskId ?? effectiveLastRun?.taskId ?? null;

            return (
              <li key={tool.name} className="py-4">
                {/* Row 1: name · status badge · action buttons */}
                <div className="flex items-center justify-between gap-x-4">
                  <div className="flex min-w-0 items-center gap-x-3">
                    <span className="truncate font-mono text-sm font-semibold text-gray-900">
                      {tool.name}
                    </span>
                    <StatusBadge job={job} lastRun={effectiveLastRun} isTriggering={isTriggering} />
                  </div>
                  <div className="flex shrink-0 items-center gap-x-2">
                    {logsTaskId && (
                      <Button variant="secondary" onClick={() => openLogModal(tool.name, logsTaskId)}>
                        Logs
                      </Button>
                    )}
                    <Button variant={isProduction ? 'danger' : 'secondary'} onClick={() => { setConfirmParams({}); setConfirmationModal(tool); }} disabled={isBusy}>
                      {isTriggering ? 'Queuing…' : 'Run now'}
                    </Button>
                  </div>
                </div>

                {/* Row 2: description · last-run meta */}
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                  {tool.description && (
                    <span className="truncate">{tool.description}</span>
                  )}
                  <span className="ml-auto flex shrink-0 items-center gap-x-3">
                    {displayTimestamp && (
                      <DateDisplay
                        className="text-[length:inherit]"
                        utcValue={new Date(displayTimestamp).getTime()}
                        displayInUTC={false}
                        optionOverrides={{ second: undefined }}
                      />
                    )}
                    {!showingCurrentRun && effectiveLastRun?.createdBy && (
                      <span>by {effectiveLastRun.createdBy}</span>
                    )}
                    {duration && <span>{duration}</span>}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

function StatusBadge({ job, lastRun, isTriggering }: { job: JobState | undefined; lastRun: LastRun | null; isTriggering: boolean }) {
  if (isTriggering) {
    return <span className="inline-flex items-center gap-x-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Queuing…</span>;
  }
  if (job && !TERMINAL_STATUSES.includes(job.status)) {
    if (job.status === 'STARTED') {
      return (
        <span className="inline-flex items-center gap-x-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
          Running
        </span>
      );
    }
    return <span className="inline-flex items-center gap-x-1.5 rounded-full bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-700">Queued</span>;
  }
  if (!lastRun) return null;
  if (lastRun.status === 'FINISHED' && lastRun.result?.success) {
    return (
      <span className="inline-flex items-center gap-x-1.5 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
        Success
      </span>
    );
  }
  const errMsg = lastRun.result?.error ?? 'unknown error';
  return (
    <span
      className="inline-flex items-center gap-x-1.5 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700"
      title={errMsg}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
      Failed
    </span>
  );
}
